/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  InitialConfigType,
  InitialEditorStateType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer';
import { LexicalEditor, ParagraphNode, TextNode } from 'lexical';

import { FlashMessageContext } from './context/FlashMessageContext';
import { SettingsContext } from './context/SettingsContext';
import { SharedAutocompleteContext } from './context/SharedAutocompleteContext';
import { SharedHistoryContext } from './context/SharedHistoryContext';
import Editor from './Editor';
import PlaygroundNodes from './nodes/PlaygroundNodes';
import { TableContext } from './plugins/TablePlugin';
import PlaygroundEditorTheme from './themes/PlaygroundEditorTheme';
import { OnChangePlugin, OnChangePluginProps } from './OnChangePlugin';
import clsx from 'clsx';
import { LocalizableTextNode } from './nodes/Localizable/LocalizableTextNode.tsx';
import { useContext, useEffect, useRef } from 'react';
import { PageDesignerContext } from '../DesignerNewPage.tsx';
import { ProtectedParagraphNode } from './nodes/Localizable/ProtectedParagraphNode.ts';
import { LinkNode } from '@lexical/link';
import { LocalizationLinkNode } from './nodes/Localizable/LocalizationLinkNode.ts';

console.warn(
  'If you are profiling the playground app, please ensure you turn off the debug view. You can disable it by pressing on the settings control in the bottom-left of your screen and toggling the debug view setting.',
);

export const LexicalEditorIntegration = (props: {
  isPageDesigner: boolean;
  isNewPage?: boolean;
  onChange?: OnChangePluginProps['onChange'];
  initialState?: InitialEditorStateType;
  editable?: boolean;
  setActiveEditor?: (editor: LexicalEditor) => void;
}) => {
  const pageDesignerContext = useContext(PageDesignerContext);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const linkEditorRef = useRef<HTMLDivElement | null>(null);

  const initialConfig: InitialConfigType = {
    // editorState: emptyEditor,
    namespace: 'Page Editor',
    nodes: [
      ...PlaygroundNodes,
      {
        replace: TextNode,
        with: (node: LocalizableTextNode) => {
          return new LocalizableTextNode(
            node.__text,
            pageDesignerContext?.defaultLanguage ?? null,
            pageDesignerContext?.pageEditorLanguage ?? null,
          );
        },
        withKlass: LocalizableTextNode,
      },
      {
        replace: ParagraphNode,
        with: (node: ProtectedParagraphNode) => {
          return new ProtectedParagraphNode();
        },
        withKlass: ProtectedParagraphNode,
      },
      {
        replace: LinkNode,
        with: (node: LinkNode) => {
          return new LocalizationLinkNode(node.getURL());
        },
        withKlass: LocalizationLinkNode,
      },
    ],
    onError: (error: Error) => {
      throw error;
    },
    theme: PlaygroundEditorTheme,
    editorState: props.initialState,
    editable: props.editable,
  };

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        const linkEditorElement = linkEditorRef.current;

        if (!linkEditorElement) {
          return;
        }

        linkEditorElement.style.transform = 'translate(-10000px, -10000px)';
        linkEditorElement.style.opacity = '0';
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [containerRef.current, linkEditorRef.current]);

  return (
    <SettingsContext>
      <FlashMessageContext>
        <LexicalComposer initialConfig={initialConfig}>
          <SharedHistoryContext>
            <TableContext>
              <SharedAutocompleteContext>
                <div
                  ref={containerRef}
                  className={clsx(
                    'editor-shell',
                    !props.editable && 'editor-shell-readonly',
                  )}
                >
                  <OnChangePlugin onChange={props.onChange} />
                  <Editor
                    isPageDesigner={props.isPageDesigner}
                    setActiveEditor={props.setActiveEditor}
                    linkEditorRef={linkEditorRef}
                  />
                </div>
              </SharedAutocompleteContext>
            </TableContext>
          </SharedHistoryContext>
        </LexicalComposer>
      </FlashMessageContext>
    </SettingsContext>
  );
};
